import { workspaceV2Redirect } from 'dashboard/helper/workspaceV2';
import { createRouter, createWebHistory } from 'vue-router';

import { frontendURL } from '../helper/URLHelper';
import dashboard from './dashboard/dashboard.routes';
import store from 'dashboard/store';
import {
  routeFeatureFlagBlocked,
  validateLoggedInRoutes,
} from '../helper/routeHelpers';
import { ACCOUNT_ENTRY_PATH } from 'dashboard/constants/globals';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import AnalyticsHelper from '../helper/AnalyticsHelper';
import {
  rememberMcpLoginReturn,
  consumeMcpLoginReturn,
} from '../helper/mcpLoginReturn';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { NOT_FOUND_ROUTE } from './dashboard/dashboard.routes';

const ONBOARDING_STEPS = ['account_details', 'enrichment'];
// Conta nova com a flag onboarding_visor: a entrada é o fluxo /comecar, mas a
// pessoa pode sair para conectar algo e voltar; o progresso fica salvo na conta.
const VISOR_ONBOARDING_STEP = 'visor';
const VISOR_ONBOARDING_PATH = 'comecar';
const routes = [...dashboard.routes];

export const router = createRouter({ history: createWebHistory(), routes });

// Rota sem par no router (ex.: /app/accounts/3/team-chat) chega sem params,
// então o id da conta vem do próprio caminho.
const accountIdFromPath = path => {
  const match = /^\/app\/accounts\/(\d+)(?:\/|$)/.exec(path || '');
  return match ? Number(match[1]) : null;
};

// Aviso de endereço inexistente. Na primeira carga o SnackbarContainer só
// monta depois do setUser, então o aviso espera haver quem escute (até 3 s).
const alertWhenSnackbarReady = (message, attempts = 30) => {
  if (emitter.all.get('newToastMessage')?.length) {
    useAlert(message, { usei18n: true });
    return;
  }
  if (attempts > 0) {
    setTimeout(() => alertWhenSnackbarReady(message, attempts - 1), 100);
  }
};

export const validateAuthenticateRoutePermission = async (to, next) => {
  const { isLoggedIn, getCurrentUser: user } = store.getters;

  if (!isLoggedIn) {
    if (to.name === 'mcp_authorize') rememberMcpLoginReturn(to.fullPath);
    next(false);
    return window.location.assign('/app/login');
  }

  // This also resumes after MFA and SSO, whose login flows land on /app/.
  if (to.name === 'mcp_authorize') {
    consumeMcpLoginReturn();
    return next();
  }
  const mcpReturn = consumeMcpLoginReturn();
  if (mcpReturn) return next(mcpReturn);

  const { accounts = [], account_id: accountId } = user;

  if (!accounts.length) {
    if (to.name === 'no_accounts') {
      return next();
    }
    return next(frontendURL('no-accounts'));
  }

  // Sem accountId nos params (ou endereço inexistente), vale a conta do
  // caminho se o usuário tiver acesso a ela; senão, a conta ativa.
  const isNotFound = to.name === NOT_FOUND_ROUTE;
  const pathAccountId = accountIdFromPath(to.path);
  const pathAccountIsMine = accounts.some(a => a.id === pathAccountId);
  const routeAccountId = Number(
    (!isNotFound && to.params?.accountId) ||
      (pathAccountIsMine ? pathAccountId : accountId)
  );
  const userAccount = accounts.find(a => a.id === routeAccountId);
  const isAdmin = userAccount?.role === 'administrator';
  const isActive = userAccount?.status === 'active';
  const needsOnboarding =
    ONBOARDING_STEPS.includes(userAccount?.onboarding_step) &&
    isAdmin &&
    isActive;
  const inVisorOnboarding =
    userAccount?.onboarding_step === VISOR_ONBOARDING_STEP &&
    isAdmin &&
    isActive;

  // Sem rota pedida (/app, /app/accounts/:id) ou endereço que não existe
  // (link velho, rota removida): a entrada é a Central. No endereço
  // inexistente a pessoa é avisada, para não achar que clicou errado.
  if (to.name === 'no_accounts' || !to.name || isNotFound) {
    let target = needsOnboarding ? 'onboarding' : ACCOUNT_ENTRY_PATH;
    if (inVisorOnboarding) target = VISOR_ONBOARDING_PATH;
    if (isNotFound && !needsOnboarding && !inVisorOnboarding) {
      alertWhenSnackbarReady('WORKSPACE_V2.ADDRESS_GONE');
    }
    return next(frontendURL(`accounts/${routeAccountId}/${target}`));
  }

  if (needsOnboarding && !isOnOnboardingView(to)) {
    return next(frontendURL(`accounts/${routeAccountId}/onboarding`));
  }
  if (!needsOnboarding && isOnOnboardingView(to) && !inVisorOnboarding) {
    return next(
      frontendURL(`accounts/${routeAccountId}/${ACCOUNT_ENTRY_PATH}`)
    );
  }

  const accountFeatures =
    store.getters['accounts/getAccount']?.(routeAccountId)?.features;
  const nextRoute = validateLoggedInRoutes(
    to,
    store.getters.getCurrentUser,
    accountFeatures
  );
  return nextRoute ? next(frontendURL(nextRoute)) : next();
};

export const initalizeRouter = () => {
  // Preserve consent even when the boot-time session check redirects to login.
  if (
    window.location.pathname === '/app/mcp/authorize' &&
    window.location.search
  ) {
    rememberMcpLoginReturn(window.location.pathname + window.location.search);
  }
  const userAuthentication = store.dispatch('setUser');

  router.beforeEach(async (to, _from, next) => {
    AnalyticsHelper.page(to.name || '', {
      path: to.path,
      name: to.name,
    });

    await userAuthentication;
    const continueNavigation = result => {
      if (result !== undefined) return next(result);
      const v2Target = workspaceV2Redirect(to, _from, router.resolve);
      return v2Target ? next(v2Target) : next();
    };
    await validateAuthenticateRoutePermission(to, continueNavigation, store);
  });

  // As features da conta chegam depois da primeira navegação (o App busca a
  // conta ao montar). Quando chegam, ou mudam, a rota atual é conferida de
  // novo, para a URL direta de um módulo desligado não ficar aberta.
  store.watch(
    (_state, getters) => {
      const accountId = router.currentRoute.value.params?.accountId;
      if (!accountId) return undefined;
      return getters['accounts/getAccount'](accountId)?.features;
    },
    features => {
      const route = router.currentRoute.value;
      if (!routeFeatureFlagBlocked(route, features)) return;
      const target = validateLoggedInRoutes(
        route,
        store.getters.getCurrentUser,
        features
      );
      if (target) router.replace(frontendURL(target));
    }
  );
};

export default router;
